import { useState } from 'react';
import { Button, LEVEL_UI } from '@/ui';
import { useAuth } from '@/hooks/useAuth';
import { useFecharComConfirmacao } from '@/hooks/useFecharComConfirmacao';
import { ConfirmarDescarte } from '@/components/ConfirmarDescarte';
import { canClose, canReceberBaixa } from '@/domain/tarp/status';
import {
  fluxoDaTarp,
  nivelEfetivoVisivel,
  subtituloDaTarp,
  podeEnquadrarAnm,
  tarpEntry,
  tituloDaTarp,
  validateBaixa,
  type BaixaInput,
  type EnquadramentoAnm,
} from '@/domain';
import { novaAcaoDraft, type AcaoDraft } from '@/domain/planos/acoes';
import { diaEmBrasilia } from '@/domain/fuso-brasilia';
import type { Plano } from '@/domain/planos';
import { novaAcaoId } from '@/components/plano/acaoId';
import type { TarpRecord } from '@/services/datasources';
import { DarBaixaForm } from './DarBaixaForm';
import { Modal } from './Modal';
import { TarpFlowRunner } from './TarpFlowRunner';
import { TarpStatusPill } from './TarpStatusPill';


/**
 * Detalhe do TARP em duas telas (fiel ao V8), com gating de perfil (regra #4):
 * - Tela A (revisão): fluxo percorrido read-only + rodapé [Fechar] [Dar Baixa];
 * - Tela B (baixa): "Dar Baixa na TARP" (3 blocos + autorização) + rodapé [Voltar] [Concluir].
 * Operador nunca encerra; só RT/admin veem a baixa (a RLS garante server-side).
 */
export function TarpDetailModal({
  tarp,
  planosVinculaveis = [],
  onClose,
  onConclude,
  onBaixa,
  concluding,
  closing,
  error,
  iniciarRegistrando = false,
}: {
  tarp: TarpRecord;
  /** Planos já vinculados a esta TARP (GUPM-213 Extra 2) — habilita "vincular existente". */
  planosVinculaveis?: Plano[];
  onClose: () => void;
  onConclude: () => void;
  onBaixa: (input: BaixaInput, totpCode: string) => void;
  concluding: boolean;
  closing: boolean;
  error: string | null;
  /** Abre já no formulário de baixa (Tela B), pulando a revisão. **GUPM-190 T7
   *  (emenda ao 184):** clicar no CORPO do card abre a Tela A read-only; o
   *  orquestrador só passa `true` quando o RT usa o atalho "Dar Baixa" do card
   *  (refino T7). Default false → read-only. A guarda de status (F1: não pula o
   *  protocolo do operador) segue valendo mesmo com `true`. */
  iniciarRegistrando?: boolean;
}) {
  const { profile } = useAuth();
  const rtCanClose = canClose(profile);
  const [parecer, setParecer] = useState('');
  const [anmAplica, setAnmAplica] = useState(false);
  const [enquadramento, setEnquadramento] = useState<EnquadramentoAnm | null>(null);
  const [planoAplica, setPlanoAplica] = useState(false);
  const [acoes, setAcoes] = useState<AcaoDraft[]>([]);
  const [planoExistenteId, setPlanoExistenteId] = useState<string | null>(null);
  // Abertura do plano da baixa = dia de Brasília ao abrir o modal (GUPM-332). A antiga
  // constante de módulo congelava o carregamento da página e cortava em UTC: depois
  // das 21h daqui o `min` da data máxima já era amanhã.
  const [aberturaDoPlano] = useState(() => diaEmBrasilia(new Date()));
  const [totpCode, setTotpCode] = useState('');

  // Fiel ao `setCloseTarpPlano` do V8: marcar "Sim, definir ações" já cria a 1ª ação
  // em branco; "Não se aplica" limpa. A 1ª ação é permanente no editor (garante ≥1).
  function handlePlanoAplica(v: boolean) {
    setPlanoAplica(v);
    if (v && acoes.length === 0) setAcoes([novaAcaoDraft(novaAcaoId())]);
    if (!v) {
      setAcoes([]);
      setPlanoExistenteId(null);
    }
  }
  const [emergencia, setEmergencia] = useState(false);
  const [registrando, setRegistrando] = useState(iniciarRegistrando);
  // A baixa só é oferecida a RT/admin E numa TARP pós-handoff (CONCLUIDA_AGUARDANDO/
  // EM_ANALISE_RT). Sem o gate de status, o RT abriria a baixa de uma TARP ainda
  // ABERTA, pulando a conclusão do operador (GUPM-184 self-review F1). O trigger
  // tarp_records_write_guard reforça server-side.
  const canRegistrarParecer = rtCanClose && canReceberBaixa(tarp.status);
  // Operador nunca vê o form; RT só entra em baixa a partir de um estado válido.
  const emBaixa = registrando && canRegistrarParecer;

  // GUPM-207: accent pelo nível EFETIVO (EMERGÊNCIA se o fluxo escalou ao paebm).
  // `Visivel`: rascunho EM_ABERTURA não escala (fix #118) — guarda defensiva (hoje o
  // detalhe de um rascunho é interceptado antes de abrir este modal).
  const u = LEVEL_UI[nivelEfetivoVisivel(tarp)];
  const entry = tarpEntry(tarp.tarpCode);
  // C-88 — mesmo título e fluxo que a TARP teve na abertura (tipo do instrumento).
  const flow = fluxoDaTarp(tarp.tarpCode, tarp.instrumentType) ?? undefined;
  const titulo = tituloDaTarp(tarp.tarpCode, tarp.instrumentType);
  const encerrada = tarp.status === 'ENCERRADA';

  const baixaInput: BaixaInput = {
    parecer,
    enquadramento: anmAplica ? enquadramento : null,
    damId: tarp.damId,
    planoAplica,
    acoes: planoAplica ? acoes : [],
    planoExistenteId: planoAplica ? planoExistenteId : null,
  };
  // GUPM-295: UMA lista para os dois usos. `podeConcluir` derivava de três condições
  // soltas e só a primeira produzia texto — que nem era renderizado. Derivando o botão
  // da mesma lista que a tela exibe, "bloqueado" e "sem motivo à vista" não podem
  // divergir: se trava, o RT lê por quê.
  const motivosBloqueio = [
    ...validateBaixa(baixaInput),
    ...(anmAplica && !enquadramento
      ? ['Escolha o enquadramento ANM (NE-1, NE-2 ou NE-3) ou marque "Não se aplica".']
      : []),
    ...(totpCode.trim().length === 0 ? ['Informe o código TOTP de autorização.'] : []),
  ];
  const podeConcluir = motivosBloqueio.length === 0;

  // C-91: ×, Esc, "Fechar" e clique fora com a baixa preenchida perguntam antes de
  // descartar (o preenchido sobrevive ao "← Voltar", então vale nas duas telas).
  const saida = useFecharComConfirmacao({
    preenchido:
      parecer.trim() !== '' || anmAplica || enquadramento !== null || planoAplica ||
      acoes.length > 0 || planoExistenteId !== null || totpCode !== '',
    ocupado: closing || concluding,
    fechar: onClose,
  });

  const footer = encerrada ? (
    <div className="flex justify-end">
      <Button variant="ghost" onClick={saida.pedirFechar}>Fechar</Button>
    </div>
  ) : emBaixa ? (
    <div className="flex gap-2">
      <Button variant="ghost" onClick={() => setRegistrando(false)}>← Voltar</Button>
      <Button
        variant="green"
        full
        disabled={closing || !podeConcluir}
        onClick={() => onBaixa(baixaInput, totpCode)}
      >
        {closing ? 'Encerrando…' : '✅ Concluir'}
      </Button>
    </div>
  ) : canRegistrarParecer ? (
    <div className="flex gap-2">
      <Button variant="ghost" onClick={saida.pedirFechar}>Fechar</Button>
      <Button variant="green" full onClick={() => setRegistrando(true)}>
        📝 Dar Baixa
      </Button>
    </div>
  ) : tarp.status === 'ABERTA' ? (
    <div className="flex justify-end">
      <Button full disabled={concluding} onClick={onConclude}>
        {concluding ? 'Encaminhando…' : 'Concluir e encaminhar ao RT'}
      </Button>
    </div>
  ) : (
    <div className="flex justify-end">
      <Button variant="ghost" onClick={saida.pedirFechar}>Fechar</Button>
    </div>
  );

  return (
    <Modal
      title={emBaixa ? 'Dar Baixa na TARP' : (titulo ?? `TARP ${tarp.tarpCode}`)}
      subtitle={
        emBaixa
          ? 'Formulário de baixa — preenchimento do responsável técnico.'
          : (subtituloDaTarp(tarp.tarpCode, tarp.instrumentType) ?? undefined)
      }
      accent={u.color}
      headerRight={<TarpStatusPill status={tarp.status} />}
      onClose={saida.pedirFechar}
      footer={footer}
    >
      <div className="space-y-3">
        {/* Contexto (barragem/instrumento) — sempre visível, inclusive na baixa. */}
        <div
          className="flex items-center gap-3 rounded-lg border p-2.5"
          style={{ borderColor: u.border, background: u.bg }}
        >
          <div className="min-w-0">
            <div className="text-[9px] font-bold uppercase tracking-wider text-jf-dim">Barragem</div>
            <div className="text-sm font-black leading-tight text-white">
              {tarp.damCode ? `Barragem ${tarp.damCode}` : 'Ainda não identificada'}
            </div>
          </div>
          {tarp.instrumentCode && (
            <div className="ml-auto text-right">
              <div className="text-[9px] font-bold uppercase tracking-wider text-jf-dim">Instrumento</div>
              <div className="text-xs font-bold text-white">{tarp.instrumentCode}</div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 text-[11px] text-jf-muted">
          <span className="font-mono font-bold" style={{ color: u.color }}>{tarp.tarpCode}</span>
          <span aria-hidden>·</span>
          Aberto em {new Date(tarp.openedAt).toLocaleString('pt-BR')}
        </div>

        {emBaixa ? (
          <DarBaixaForm
            errors={motivosBloqueio}
            parecer={parecer}
            onParecer={setParecer}
            anmAplica={anmAplica}
            onAnmAplica={setAnmAplica}
            enquadramento={enquadramento}
            onEnquadramento={setEnquadramento}
            planoAplica={planoAplica}
            onPlanoAplica={handlePlanoAplica}
            acoes={acoes}
            onAcoes={setAcoes}
            abertura={aberturaDoPlano}
            planosExistentes={planosVinculaveis}
            planoExistenteId={planoExistenteId}
            onPlanoExistente={setPlanoExistenteId}
            totpCode={totpCode}
            onTotpCode={setTotpCode}
            barragemDefinida={podeEnquadrarAnm(tarp.damId)}
          />
        ) : (
          <>
            {/* Warning do protocolo — banner de alerta no topo (fiel ao V8), na revisão. */}
            {entry?.warning && !encerrada && (
              <div
                className="rounded-lg border px-3 py-2 text-[13px] font-semibold leading-snug"
                style={{ borderColor: u.border, background: u.bg, color: u.color }}
              >
                {entry.warning}
              </div>
            )}

            {/* Fluxo GRAVADO, read-only (só o percorrido), em todo status — inclusive ABERTA.
                C-92: em ABERTA ele era editável sem `onStateChange`, então o que o operador
                marcava aqui nunca era gravado. Só se chega a ABERTA pelo editor, que exige
                desfecho terminal e grava o fluxo antes de confirmar: não há o que responder. */}
            {flow && !encerrada && (
              <TarpFlowRunner
                flow={flow}
                initialState={tarp.flowState ?? undefined}
                mode="review"
                onOutcomeChange={(o) => {
                  if (o === 'paebm') setEmergencia(true);
                }}
              />
            )}

            {emergencia && !encerrada && (
              <div className="rounded-lg border border-level-emergencia/50 bg-level-emergencia/10 p-3 text-xs font-bold text-red-200">
                🆘 Fluxo indicou EMERGÊNCIA / PAEBM. Acione as sirenes e a cadeia de emergência.
              </div>
            )}

            {encerrada && (
              <div className="rounded-lg border border-level-normal/30 bg-level-normal/10 p-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-level-normal">✅ Baixa registrada</div>
                  {tarp.anmEnquadramento && (
                    <span className="rounded-full border border-jf-yellow/40 bg-jf-yellow/15 px-2 py-0.5 text-[10px] font-bold text-jf-yellow">
                      ANM {tarp.anmEnquadramento}
                    </span>
                  )}
                </div>
                {tarp.closureComment && <p className="mt-1 text-xs text-jf-muted">{tarp.closureComment}</p>}
                {tarp.planoAcao && (
                  <p className="mt-1 text-[11px] text-jf-muted">
                    <span className="font-bold text-jf-dim">Plano de ação: </span>
                    {tarp.planoAcao}
                  </p>
                )}
                {tarp.closedAt && (
                  <p className="mt-1 text-[10px] text-jf-dim">
                    em {new Date(tarp.closedAt).toLocaleString('pt-BR')}
                    {tarp.baixaTotpVerified ? ' · TOTP ✓' : ' · TOTP não verificado (POC)'}
                  </p>
                )}
              </div>
            )}

            {!rtCanClose && !encerrada && tarp.status !== 'ABERTA' && (
              <div className="rounded-lg border border-sky-500/30 bg-sky-500/10 p-3 text-xs text-sky-200">
                Concluído — aguardando análise e baixa do RT.
              </div>
            )}
          </>
        )}

        {error && <p className="text-xs text-level-emergencia">{error}</p>}
        {saida.perguntando && <ConfirmarDescarte onContinuar={saida.continuar} onDescartar={saida.descartar} />}
      </div>
    </Modal>
  );
}
